import { type BigDecimal, absoluteValue, plus } from '@0xtorch/big-decimal'
import type { Action, Transfer } from '@0xtorch/core'
import {
  ApiPassphraseRequiredException,
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { bitgetAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

const ignoreTypes = new Set([
  // 別エンドポイント (depositHistory / withdrawalHistory) で処理されるため重複させない
  'Deposit',
  'Ordinary Withdrawal',
  'withdraw_fee_deduct',
  'Withdrawal',
  // アカウント間の内部振替 (資産の増減なし)
  'Transfer in',
  'Transfer out',
  // 資産運用 (Earn / PoolX 等) の元本移動。利息は batch_interest_user_in 等で別途計上される
  'financial_double_buy_user_out',
  'financial_lock_out',
  'financial_pos_out',
  'financial_pos_pledge_user_in',
  'financial_rede_in',
  'financial_subs_out',
  'financial_unlock_in',
  'financial_user_out',
  'poolx_redem_in',
  'poolx_sub_out',
  'Redemption',
  // ロック / アンロック操作 (資産の増減なし)
  'System lock-up',
  'The locked order is returned to the system',
  'Unlock locked order',
  'User lock-up',
])

// Pattern-based ignore types for dynamic Bitget API types
const ignoreTypePatterns = [
  // FINANCIAL_ON_CHAIN_ELITE_*_USER_FREEZE_IN/OUT or _USER__FREEZE_IN/OUT (freeze/unfreeze operations)
  /^FINANCIAL_ON_CHAIN_ELITE_.*_USER__?FREEZE_(IN|OUT)$/,
  // FINANCIAL_ON_CHAIN_ELITE_REDEEM_USER_IN/OUT (redemption of staked tokens)
  /^FINANCIAL_ON_CHAIN_ELITE_REDEEM_USER_(IN|OUT)$/,
  // FINANCIAL_SINGLE_REGULAR_USER_FREEZE_IN/OUT (freeze/unfreeze for regular staking)
  /^FINANCIAL_SINGLE_REGULAR_USER_FREEZE_(IN|OUT)$/,
]

const isIgnoreType = (type: string): boolean => {
  if (ignoreTypes.has(type)) {
    return true
  }
  return ignoreTypePatterns.some((pattern) => pattern.test(type))
}

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  apiPassphrase,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }
  if (!apiPassphrase) {
    throw new ApiPassphraseRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    apiPassphrase,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/api/v2/tax/spot-record',
    query: {
      startTime: from?.toString() ?? '',
      endTime: to?.toString() ?? '',
      limit: config.limit.toString(),
    },
    responseSchema,
  })

  // 昇順 sort by ts
  const sortedData = response.data.sort((a, b) => a.ts - b.ts)

  // データ処理
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  // Group items by timestamp and spotTaxType
  const groupedItems = new Map<string, typeof sortedData>()

  for (const item of sortedData) {
    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? item.ts : Math.min(fetchedFrom, item.ts)
    fetchedTo = fetchedTo === undefined ? item.ts : Math.max(fetchedTo, item.ts)

    const type = parseType(item.spotTaxType)

    // Skip ignore types
    if (isIgnoreType(type)) {
      continue
    }

    const key = `${item.ts}_${type}`
    const group = groupedItems.get(key) || []
    group.push(item)
    groupedItems.set(key, group)
  }

  // Process grouped items
  for (const [key, items] of groupedItems) {
    if (items.length === 0) {
      continue
    }
    const type = key.split('_').slice(1).join('_')
    const ts = items[0].ts

    switch (type) {
      // trade
      case 'Trade': {
        // Sum amounts by coin
        const coinAmounts = new Map<string, BigDecimal>()

        for (const item of items) {
          const existing = coinAmounts.get(item.coin)
          if (existing) {
            coinAmounts.set(
              item.coin,
              plus(existing, plus(item.amount, item.fee)),
            )
          } else {
            coinAmounts.set(item.coin, plus(item.amount, item.fee))
          }
        }

        // Validate that we have both positive and negative amounts
        const hasPositive = Array.from(coinAmounts.values()).some(
          (amount) => amount.value > 0n,
        )
        const hasNegative = Array.from(coinAmounts.values()).some(
          (amount) => amount.value < 0n,
        )

        if (!hasPositive || !hasNegative) {
          continue
        }

        // Create trade action
        const transfers: Transfer[] = []
        for (const [coin, amount] of coinAmounts) {
          if (amount.value === 0n) {
            continue
          }

          const asset = getAsset(coin)
          if (!asset) {
            throw new AssetNotFoundException(coin)
          }

          transfers.push({
            asset,
            amount: absoluteValue(amount),
            direction: amount.value > 0n ? 'in' : 'out',
          })
        }

        actions.push({
          comment: `ID: ${items.map((i) => i.id).join(' / ')}`,
          evidence: 'system-rule',
          order: 0,
          source: `${bitgetAccountCategory}_${config.type}_${type}_${ts}`,
          type: 'trade',
          timestamp: ts,
          transfers,
        })
        break
      }
      // airdrop
      case 'Airdrop Reward-A':
      case 'Airdrop Reward-B': {
        // Process each item individually for airdrop
        for (const item of items) {
          if (item.amount.value > 0n) {
            const asset = getAsset(item.coin)
            if (!asset) {
              throw new AssetNotFoundException(item.coin)
            }

            actions.push({
              evidence: 'system-rule',
              order: 0,
              source: `${bitgetAccountCategory}_${config.type}_${type.replaceAll(' ', '_')}_${item.id}`,
              type: 'airdrop',
              timestamp: item.ts,
              transfers: [
                {
                  asset,
                  amount: item.amount,
                  direction: 'in',
                },
              ],
            })
          } else if (item.amount.value < 0n) {
            throw new Error('Airdrop amount cannot be negative')
          }
        }
        break
      }
      // interest
      case 'batch_interest_user_in':
      case 'Interest': {
        // Process each item individually for interest
        for (const item of items) {
          if (item.amount.value > 0n) {
            const asset = getAsset(item.coin)
            if (!asset) {
              throw new AssetNotFoundException(item.coin)
            }

            actions.push({
              evidence: 'system-rule',
              order: 0,
              source: `${bitgetAccountCategory}_${config.type}_${type}_${item.id}`,
              type: 'interest',
              timestamp: item.ts,
              transfers: [
                {
                  asset,
                  amount: item.amount,
                  direction: 'in',
                },
              ],
            })
          } else if (item.amount.value < 0n) {
            throw new Error('Interest amount cannot be negative')
          }
        }
        break
      }
      // fee
      case 'Exchange spending': {
        // Process each item individually for fee
        for (const item of items) {
          if (item.amount.value < 0n) {
            const asset = getAsset(item.coin)
            if (!asset) {
              throw new AssetNotFoundException(item.coin)
            }

            actions.push({
              comment: item.spotTaxType,
              evidence: 'system-rule',
              order: 0,
              source: `${bitgetAccountCategory}_${config.type}_${type}_${item.id}`,
              type: 'fee',
              timestamp: item.ts,
              transfers: [
                {
                  asset,
                  amount: absoluteValue(item.amount),
                  direction: 'out',
                },
              ],
            })
          } else if (item.amount.value > 0n) {
            throw new Error('Fee amount cannot be positive')
          }
        }
        break
      }
      // gift or fee (negative amount means clawback/reversal)
      // キャンペーン等のアクティビティ報酬の受取。`rewards_user_in` と同じく資産増加として扱う
      case 'ACTIVITY_ASSET_REWARD_USER_IN':
      case 'Crypto Voucher Distribution':
      case 'Increase exchange rate':
      case 'Rebate rewards':
      case 'rebate_coupon_activity_user_in':
      case 'Reduce exchange rate':
      case 'rewards_user_in':
      case 'strategy_coupon_liquidation_parent_user_in':
      // 支払った取引手数料の払い戻し。`Rebate rewards` と同じく資産増加として扱う
      case 'Trading fee rebate': {
        for (const item of items) {
          if (item.amount.value === 0n) {
            continue
          }
          const asset = getAsset(item.coin)
          if (!asset) {
            throw new AssetNotFoundException(item.coin)
          }
          actions.push({
            comment: item.spotTaxType,
            evidence: 'system-rule',
            order: 0,
            source: `${bitgetAccountCategory}_${config.type}_${type}_${item.id}`,
            type: item.amount.value > 0n ? 'gift' : 'fee',
            timestamp: item.ts,
            transfers: [
              {
                asset,
                amount: absoluteValue(item.amount),
                direction: item.amount.value > 0n ? 'in' : 'out',
              },
            ],
          })
        }
        break
      }
      // evidence = none
      case '':
      case 'Activity (body) - User freeze':
      case 'Deduction':
      case 'recharge_limit_withdraw_frozen':
      case 'Release':
      case 'Repayment':
      case 'Return': {
        // evidence = none で処理
        for (const item of items) {
          const total = plus(item.amount, item.fee)
          if (total.value === 0n) {
            continue
          }
          const asset = getAsset(item.coin)
          if (!asset) {
            throw new AssetNotFoundException(item.coin)
          }
          actions.push({
            comment: item.spotTaxType,
            evidence: 'none',
            order: 0,
            source: `${bitgetAccountCategory}_${config.type}_${type}_${item.id}`,
            type: total.value > 0 ? 'gift' : 'transfer',
            timestamp: item.ts,
            transfers: [
              {
                asset,
                amount: absoluteValue(total),
                direction: total.value > 0n ? 'in' : 'out',
              },
            ],
          })
        }
        break
      }
      default: {
        throw new Error(`Unsupported type: ${type}`)
      }
    }
  }

  // Sort actions by timestamp in ascending order
  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  // Wait for rate limit (1 times/1s)
  await new Promise((resolve) => setTimeout(resolve, 1000))

  return {
    actions: sortedActions,
    count: response.data.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}

const parseType = (type: string): string => {
  if (
    type === 'Buy' ||
    type === 'Sell' ||
    type === 'Transaction fee deduct' ||
    type === 'Consumption' ||
    type === 'Gains' ||
    type === 'Exchange income' ||
    type === 'Exchange spending' ||
    type === 'FINANCIAL_ON_CHAIN_ELITE_TRADE_USER_OUT' ||
    type === 'FINANCIAL_ON_CHAIN_ELITE_TRADE_USER_IN'
  ) {
    return 'Trade'
  }
  return type
}
